import { useUser } from '../contexts/UserContext';

// Define role-based permissions
const ROLE_PERMISSIONS = {
  'admin': [
    'view_dashboard',
    'manage_cars',
    'manage_users',
    'view_analytics',
    'manage_evaluators',
    'manage_drivers',
    'view_all_data',
    'dealer_evaluator'
  ],
  'orguserdealer': [
    'view_dashboard',
    'manage_cars',
    'view_my_cars',
    'upload_pictures',
    'place_bids',
    'view_payments',
    'take_delivery',
    'view_noc'
  ],
  'evaluator': [
    'view_evaluator_home',
    'evaluate_cars',
    'upload_evaluation_images',
    'dealer_evaluator'
  ],
  'driver': [
    'view_driver_home',
    'manage_deliveries'
  ],
  'dealer_evaluator': [
    'view_evaluator_home',
    'evaluate_cars',
    'upload_evaluation_images',
    'dealer_evaluator'
  ]
};

export const usePermissions = () => {
  const { currentUser } = useUser();
  
  const getUserRole = () => {
    if (!currentUser) return null;
    
    // Check localStorage for role first (from login process)
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      return storedRole;
    }
    
    // Fallback to user object
    return currentUser.role || currentUser.roles?.[0] || 'orguserdealer';
  };

  const getUserPermissions = () => {
    const role = getUserRole();
    if (!role) return [];
    return ROLE_PERMISSIONS[role] || [];
  };

  const hasPermission = (permission) => {
    const permissions = getUserPermissions();
    return permissions.includes(permission);
  };

  const hasRole = (role) => {
    const userRole = getUserRole();
    return userRole === role || userRole === 'admin';
  };

  const hasAnyRole = (roles) => {
    const userRole = getUserRole();
    return roles.includes(userRole) || userRole === 'admin';
  };

  const canAccessRoute = (route) => {
    const userRole = getUserRole();
    const permissions = getUserPermissions();
    
    // Admin can access everything
    if (userRole === 'admin') return true;
    
    // Check if user has dealer_evaluator permission for evaluator routes
    if (route === '/evaluatorHome' && permissions.includes('dealer_evaluator')) {
      return true;
    }
    
    // Route-based access control
    const routePermissions = {
      '/DealerHome': ['orguserdealer'],
      '/CarDetail': ['orguserdealer'],
      '/mycars': ['orguserdealer'],
      '/evaluatorHome': ['dealer_evaluator'], // Only check for dealer_evaluator permission
      '/driverHome': ['driver'],
      '/analytics-test': ['orguserdealer', 'admin']
    };

    const allowedRoles = routePermissions[route];
    return allowedRoles ? allowedRoles.includes(userRole) : false;
  };

  return {
    userRole: getUserRole(),
    permissions: getUserPermissions(),
    hasPermission,
    hasRole,
    hasAnyRole,
    canAccessRoute
  };
};
